import { Eye, PencilSimple, Trash } from '@phosphor-icons/react';
import type { SavedReportConfig } from '../../types/reportBuilder';

interface ReportCardProps {
  report: SavedReportConfig;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function ReportCard({ report, onView, onEdit, onDelete }: ReportCardProps) {
  return (
    <div
      onClick={() => onView(report.id)}
      className="group relative cursor-pointer rounded-lg border border-gray-200 bg-white p-5 shadow-sm transition-all hover:border-blue-300 hover:shadow-md w-full"
    >
      <div className="mb-2 flex items-start justify-between">
        <h3 className="text-base font-semibold text-gray-900 line-clamp-1">{report.name}</h3>
        <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          <button
            onClick={(e) => { e.stopPropagation(); onView(report.id); }}
            className="rounded p-1 text-gray-400 hover:bg-blue-50 hover:text-blue-600"
            title="View"
          >
            <Eye size={16} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(report.id); }}
            className="rounded p-1 text-gray-400 hover:bg-blue-50 hover:text-blue-600"
            title="Edit"
          >
            <PencilSimple size={16} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(report.id); }}
            className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
            title="Delete"
          >
            <Trash size={16} />
          </button>
        </div>
      </div>

      {report.description && (
        <p className="mb-3 text-sm text-gray-600 line-clamp-2">{report.description}</p>
      )}

      <div className="mb-3 flex flex-wrap gap-1.5">
        {report.model_keys.map((key) => (
          <span
            key={key}
            className="inline-flex rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700"
          >
            {key}
          </span>
        ))}
      </div>

      <p className="text-sm text-gray-400">Updated {timeAgo(report.updated_at)}</p>
    </div>
  );
}
